import { Row, Cell } from "@tanstack/react-table";
import classNames from "classnames";
import { Fragment } from "react";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import { difference } from "lodash";
import { chunkBy, renderColumnValue } from "./utils";
import { TableBodyProps } from "./TableBody";
import { MobileOpenActionButton } from "./MobileOpenActionButton";
import { MobileExpandActionButton } from "./MobileExpandActionButton";
import { Link } from "react-router-dom";

interface ExceptionalColumn {
  id: string;
  column: Cell<any, unknown> | undefined;
  className: string;
  position?: string;
}

interface MobileRowActionsProps {
  exceptionalColumns?: ExceptionalColumn[];
}

export function TableBodyMobile<T>({
  table,
  renderExpandedRow,
  onClickRow,
  getRowLink,
  getRowClasses
}: TableBodyProps<T>) {
  const renderExpanded = (row: Row<T>) => !!renderExpandedRow && row.getIsExpanded();
  const { isCompactLayout } = useIsCompactLayout();

  return chunkBy(table.getRowModel().rows, (row) => (renderExpanded(row) ? row.id : false)).map((chunk) => (
    <div className="table-mobile" key={chunk[0]?.id}>
      {chunk.map((row) => {
        const rowLink = getRowLink?.(row);
        const customRowClasses = getRowClasses?.(row) || "";
        const hasCustomRowClasses = !!customRowClasses && customRowClasses !== "group-last:h-[60px]";

        function getColumn(column: string) {
          return row.getVisibleCells().find((cell) => cell.column.columnDef.id === column);
        }

        const exceptionalColumns = [
          { id: "select", column: getColumn("select"), className: "" },
          { id: "avatar", column: getColumn("avatar") },
          { id: "actions", column: getColumn("actions"), position: "end" }
        ];

        const contentCells =
          difference(
            row.getVisibleCells(),
            exceptionalColumns.map((exception) => exception.column)
          ) || [];

        const MobileRowActions = ({ exceptionalColumns }: MobileRowActionsProps) => {
          const endPositionColumns =
            exceptionalColumns?.filter((exception) => exception.position === "end" && exception.column) ?? [];
          const useAltBorder = table.options.meta?.useAltBorder;
          return (
            <div className={classNames("flex flex-col items-center", { "pr-4": useAltBorder && isCompactLayout })}>
              {endPositionColumns.map((exception, i) => {
                const column = getColumn(exception.id);
                if (!column) return null;

                // Check if this column has a custom cell renderer
                const hasCustomCell = typeof column.column.columnDef.cell === "function";

                if (endPositionColumns.length === 1 && (onClickRow || rowLink) && !hasCustomCell) {
                  return <MobileOpenActionButton key={i} onClick={handleRowClick} />;
                }

                return (
                  <div
                    key={i}
                    className={classNames(exception.className, exception.column?.column.columnDef.meta?.className)}
                  >
                    {exception.column && renderColumnValue(exception.column)}
                  </div>
                );
              })}

              {(!!onClickRow || !!rowLink) && endPositionColumns.length === 0 && (
                <MobileOpenActionButton onClick={handleRowClick} />
              )}

              {renderExpandedRow && row.getCanExpand() && (
                <MobileExpandActionButton isExpanded={renderExpanded(row)} onClick={() => row.toggleExpanded()} />
              )}
            </div>
          );
        };

        const handleRowClick = (event?: React.MouseEvent) => {
          if (onClickRow) {
            onClickRow(row, event);
          }
        };

        const rowContent = (
          <div
            className={classNames("flex py-3 px-4 border-t group align-top lg:align-middle", customRowClasses, {
              "cursor-pointer hover:bg-extended-pgasilver-brand-color-opacity-20":
                !!(onClickRow || rowLink) && !hasCustomRowClasses
            })}
          >
            <div className="flex w-full">
              <div className="flex-1 flex gap-2">
                {exceptionalColumns
                  .filter((exception) => exception.position !== "end")
                  .map(
                    (exception, index) =>
                      exception.column && (
                        <div
                          key={index}
                          className={classNames(
                            exception.className,
                            exception.column?.column.columnDef.meta?.className
                          )}
                        >
                          {exception.column && renderColumnValue(exception.column)}
                        </div>
                      )
                  )}

                <div className="min-w-0 flex-1 [&_button]:!p-0 [&_button_.ml-1]:hidden [&_button_[title*='View']]:hidden [&_.tag]:!m-0">
                  {contentCells
                    .filter((c): c is Cell<T, unknown> => !!c)
                    .map((cell) => {
                      const shouldHideColumnLabel =
                        cell.column.columnDef.meta?.headerClassName?.includes("hide-header-in-column");
                      const shouldIndent = (row.original as any)?.indent === true;

                      return (
                        <div
                          key={cell.id}
                          className={classNames(
                            "block min-w-0 first:text-body-medium text-body-small first:text-sys-brand-on-surface text-sys-brand-on-surface-variant text-left mb-1 last:mb-0",
                            shouldIndent && "ml-6",
                            cell.column.columnDef.meta?.className
                          )}
                        >
                          {!shouldHideColumnLabel && cell.column.columnDef.header ? (
                            <>
                              <div
                                className={`${
                                  cell.column.columnDef.meta?.headerClassName || ""
                                } inline mr-1 whitespace-nowrap font-medium`}
                              >
                                {typeof cell.column.columnDef.header === "string" ? cell.column.columnDef.header : ""}:
                              </div>
                              <div className="inline whitespace-normal break-words [&>*]:inline-flex [&>*]:align-middle">
                                {renderColumnValue(cell, isCompactLayout)}
                              </div>
                            </>
                          ) : (
                            <div className="min-w-0 max-w-full whitespace-normal break-words">
                              {renderColumnValue(cell, isCompactLayout)}
                            </div>
                          )}
                        </div>
                      );
                    })}
                </div>
              </div>
              <MobileRowActions exceptionalColumns={exceptionalColumns as ExceptionalColumn[]} />
            </div>
          </div>
        );

        return (
          <Fragment key={row.id}>
            {rowLink ? <Link {...rowLink}>{rowContent}</Link> : <div onClick={handleRowClick}>{rowContent}</div>}
            {/* Render expanded row content on mobile, if expanded */}
            {renderExpanded(row) && (
              <div className="border-t" data-testid="body-row-expanded">
                {renderExpandedRow ? renderExpandedRow({ row }) : null}
              </div>
            )}
          </Fragment>
        );
      })}
    </div>
  ));
}
